<script setup lang="ts">
import { computed } from 'vue';

// Rendu markdown minimal et sans dépendance (repris de parcours) — gère les
// titres (#, ##, ###), le gras, l'italique, les listes «- », les «---» et les
// paragraphes. Suffisant pour afficher un avis de consentement Loi 25.
const props = defineProps<{ source: string }>();

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function renderInline(s: string): string {
  let out = escapeHtml(s);
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  return out;
}

const html = computed(() => {
  const lines = props.source.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let inList = false;
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    out.push(`<p>${paragraph.map(renderInline).join(' ')}</p>`);
    paragraph = [];
  };
  const closeList = () => {
    if (inList) {
      out.push('</ul>');
      inList = false;
    }
  };

  for (const raw of lines) {
    const line = raw.trim();

    if (line === '') {
      flushParagraph();
      closeList();
      continue;
    }

    if (line.startsWith('### ')) {
      flushParagraph(); closeList();
      out.push(`<h3>${renderInline(line.slice(4))}</h3>`);
      continue;
    }
    if (line.startsWith('## ')) {
      flushParagraph(); closeList();
      out.push(`<h2>${renderInline(line.slice(3))}</h2>`);
      continue;
    }
    if (line.startsWith('# ')) {
      flushParagraph(); closeList();
      out.push(`<h1>${renderInline(line.slice(2))}</h1>`);
      continue;
    }
    if (line === '---') {
      flushParagraph(); closeList();
      out.push('<hr />');
      continue;
    }
    if (line.startsWith('- ')) {
      flushParagraph();
      if (!inList) { out.push('<ul>'); inList = true; }
      out.push(`<li>${renderInline(line.slice(2))}</li>`);
      continue;
    }

    closeList();
    paragraph.push(line);
  }
  flushParagraph();
  closeList();
  return out.join('\n');
});
</script>

<template>
  <div class="simple-markdown" v-html="html" />
</template>

<style scoped>
.simple-markdown :deep(h1) { font-size: 1.6rem; font-weight: 600; margin: 1rem 0 0.5rem; }
.simple-markdown :deep(h2) { font-size: 1.3rem; font-weight: 600; margin: 1rem 0 0.4rem; }
.simple-markdown :deep(h3) { font-size: 1.1rem; font-weight: 600; margin: 0.75rem 0 0.3rem; }
.simple-markdown :deep(p)  { margin: 0.5rem 0; line-height: 1.55; }
.simple-markdown :deep(ul) { margin: 0.5rem 0 0.5rem 1.25rem; }
.simple-markdown :deep(li) { margin: 0.2rem 0; }
.simple-markdown :deep(hr) { border: none; border-top: 1px solid rgba(0,0,0,0.1); margin: 1rem 0; }
</style>
